/**
 * Coach Dashboard content.
 *
 * All copy and external URLs live here so they can be updated without
 * touching view code. URLs marked TODO need the real link from the Coach
 * Onboarding Hub — placeholders for now.
 */

export interface IQuickLink {
  id: string;
  title: string;
  description: string;
  url: string;
  urgency?: "primary" | "secondary";
}

export interface IChecklistItem {
  id: string;
  label: string;
}

export interface IChecklistGroup {
  id: string;
  title: string;
  items: IChecklistItem[];
}

export const toolBelt: IQuickLink[] = [
  {
    id: "concierge-hub",
    title: "Member Relationships",
    description:
      "Lead, guest, and member CRM — pipeline, conversations, tasks.",
    url: "https://app.gohighlevel.com",
    urgency: "primary"
  },
  {
    id: "pike13",
    title: "Billing and Scheduling",
    description: "Member rosters, session bookings, invoices, and waivers.",
    url: "https://startingstrengthcentennial.pike13.com",
    urgency: "primary"
  },
  {
    id: "slack",
    title: "Team Communication",
    description: "Day-to-day chat and announcements across the team.",
    url: "https://slack.com"
  },
  {
    id: "truecoach",
    title: "Online Coaching",
    description: "Programming delivery and messaging for online clients.",
    url: "https://app.truecoach.co"
  },
  {
    id: "notebooklm",
    title: "AI Coach Assistant",
    description: "Ask trained notebooks the way you'd ask a senior coach.",
    url: "https://notebooklm.google.com"
  },
  {
    id: "knowledgebase",
    title: "Standard Operating Procedures",
    description: "SOPs, training materials, and reference documents.",
    url: "https://help.ssgyms.com"
  },
  {
    id: "leadconnector",
    title: "Mobile App",
    description: "Phone access to the Member Relationships system.",
    url: "https://leadconnector.com"
  },
  {
    id: "cleaning",
    title: "Facility Care",
    description: "Daily, weekly, and monthly cleaning checklists.",
    url: "#"
  }
];

export const dailyChecklist: IChecklistGroup[] = [
  {
    id: "concierge",
    title: "Member Relationships",
    items: [
      {
        id: "c1",
        label: "Reply to missed calls, texts, and emails in your inbox"
      },
      { id: "c2", label: "Check the team inbox for missed messages" },
      { id: "c3", label: "Complete today's Dashboard tasks" },
      {
        id: "c4",
        label: "Follow up with new leads and unconverted free sessions"
      },
      {
        id: "c5",
        label: "Call members who missed their last session without notice"
      },
      {
        id: "c6",
        label:
          "Ensure ownership and task/reminder on every lead, guest, member, and hold"
      },
      {
        id: "c7",
        label:
          "Move people through the funnel (e.g. Free Session Booked → Attended)"
      },
      { id: "c8", label: "Add notes to all contacts you've interacted with" },
      { id: "c9", label: "Assign owners to unassigned contacts" },
      {
        id: "c10",
        label: "Text the group about Free Sessions conducted since last update"
      }
    ]
  },
  {
    id: "pike13",
    title: "Pike13",
    items: [
      {
        id: "p1",
        label: "Check free sessions and Intros on your schedule — 2 weeks ahead"
      },
      {
        id: "p2",
        label: "Ensure all enrolled members have an active membership"
      },
      {
        id: "p3",
        label:
          "Review holds/cancels: took effect, refund/void/credit processed, removed from roster"
      },
      { id: "p4", label: "Mark last business day members attended or no-show" },
      {
        id: "p5",
        label:
          "Mark free session / Intro / Private attendees (attended, canceled, no-show)"
      },
      {
        id: "p6",
        label: "Track missed group training sessions for your Coach logbook"
      },
      { id: "p7", label: "Call members missing 3+ sessions per month" }
    ]
  },
  {
    id: "comms",
    title: "Email & Slack",
    items: [
      { id: "e1", label: "Check and respond to messages" },
      { id: "e2", label: "Save important info to G drive" }
    ]
  },
  {
    id: "truecoach",
    title: "TrueCoach",
    items: [
      { id: "t1", label: "Respond to messages" },
      { id: "t2", label: "Respond to completed workouts" },
      { id: "t3", label: "Ensure next week's workouts are programmed" }
    ]
  },
  {
    id: "logbook",
    title: "Digital Logbook",
    items: [
      {
        id: "l1",
        label: "Enter next-workout programming for all members seen today"
      },
      {
        id: "l2",
        label:
          "Prepare milestone celebrations (plate milestones, 600/1000 lb club)"
      },
      {
        id: "l3",
        label: "Post milestone write-ups in Slack — tag Kaylah for social media"
      }
    ]
  },
  {
    id: "gym",
    title: "Gym Maintenance",
    items: [
      { id: "g1", label: "Follow opening and closing checklists" },
      { id: "g2", label: "Follow the cleaning checklist" },
      { id: "g3", label: "Send facilities requests to Kaylah" }
    ]
  }
];

export const weeklyChecklist: IChecklistItem[] = [
  { id: "w1", label: "Complete coach development tasks" },
  {
    id: "w2",
    label:
      "Check next week's roster — no double bookings or wrong-list waitlists"
  },
  {
    id: "w3",
    label: "Check waitlist 5 days out; contact members who can be moved in"
  },
  { id: "w4", label: "Meet with manager to review Coach logbook" },
  {
    id: "w5",
    label: "Meet with a Senior Coach to review programming (unless SSC)"
  },
  { id: "w6", label: "Read 20 pages in one of the three books" },
  { id: "w7", label: "Hit a PR on squat, press, or deadlift" },
  { id: "w8", label: "Check supplies, order on Amazon as needed" },
  { id: "w9", label: "Check resale merchandise inventory" },
  {
    id: "w10",
    label: "Check personal schedule; request coverage two weeks out if needed"
  }
];

export const playbooks: IQuickLink[] = [
  {
    id: "nlp",
    title: "Novice Linear Progression",
    description: "NLP quick reference guide.",
    url: "#"
  },
  {
    id: "free-session",
    title: "Free 30-Minute Session",
    description: "Step-by-step playbook for the free session.",
    url: "#"
  },
  {
    id: "intro-session",
    title: "Introductory Session",
    description: "Step-by-step playbook for the intro session.",
    url: "#"
  },
  {
    id: "member-mgmt",
    title: "Member Management",
    description: "Running your member pipeline day to day.",
    url: "#"
  }
];

export const protocols: IQuickLink[] = [
  {
    id: "lead-response",
    title: "Lead Response Protocol",
    description: "How and when to respond to inbound leads.",
    url: "#"
  },
  {
    id: "session-confirmation",
    title: "Session Confirmation Protocol",
    description: "Confirming sessions with members and prospects.",
    url: "#"
  }
];

export const notebookTopics: string[] = [
  "Starting Strength Gyms basics — programming, nutrition, retention, concierge service",
  "3 Day Advanced Programming",
  "Advanced Programming",
  "Programming the Overhead Press",
  "Programming for Retention",
  "Nutrition Priorities for Strength Trainees",
  "Managing Pain and Injuries"
];

export const podcast: IQuickLink = {
  id: "stronger-is-better",
  title: "Stronger is Better",
  description: "The Starting Strength Gyms podcast — video and audio.",
  url: "https://www.youtube.com/@StrongerIsBetter"
};

